import React from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import useReduxAction from '@hooks/useReduxAction'
import useZonesV4 from '@hooks/useZonesV4'

import { useModals, useSetState } from '@campaignhub/react-hooks'
import {
  Box, Columns, DashboardModule, LoadingModule, ModalContext, PageHeader,
} from '@campaignhub/suit-theme'

import PageContext from '@contexts/pageContext'
import EditZoneModal from '@modals/EditZoneModal'

import blankState from '@components/assets/blankState.svg'
import custom from '@styles/custom.module.scss'
import Tools from './components/Tools'
import Zone from './components/Zone'

const defaultState = {
  defaultCount: 50,
  loadNext: false,
  page: 0,
  searchFilter: '',
  showEditZoneModal: false,
}

const deleteZone = (deleteFn, setState) => {
  deleteFn().then(({ success, errors }) => {
    if (!success && errors){
      toast.warn(errors[0])
      return
    }
    toast('Zone has been deleted!')
    setState({ showEditZoneModal: false })
  })
}

const updateZoneClients = (entityParams, updateFn) => {
  updateFn(entityParams).then(({ success, errors }) => {
    if (!success && errors){
      toast.warn(errors[0])
    }
  })
}

const updateZoneProviders = (entityParams, updateFn) => {
  updateFn(entityParams).then(({ success, errors }) => {
    if (!success && errors){
      toast.warn(errors[0])
    }
  })
}

const updateZone = (zoneParams, updateFn, setState,
  linkedClients, linkedProviders, updateZoneClientsFn, updateZoneProvidersFn) => {
  updateFn(zoneParams).then(({ success, errors }) => {
    if (!success && errors){
      toast.warning(errors[0])
      return
    }

    const clients = []
    const providers = []

    linkedClients.map(client => clients.push(client.id))
    linkedProviders.map(provider => providers.push(provider.id))

    const zoneDetails = {
      id: zoneParams.id,
      linkedClients: clients,
      linkedProviders: providers,
    }

    updateZoneClients(zoneDetails, updateZoneClientsFn)
    updateZoneProviders(zoneDetails, updateZoneProvidersFn)

    setState({ showEditZoneModal: false })
    toast('Zone has been updated!')
  })
}

const callbacks = (component, setState) => {
  const componentCallbacks = {
    EditZoneModal: {
      closeModal: () => setState({ showEditZoneModal: false }),
      deleteZone: deleteFn => deleteZone(deleteFn, setState),
      updateZone: (zoneParams, updateFn, linkedClients, linkedProviders,
        updateZoneClientsFn, updateZoneProvidersFn) => updateZone(zoneParams, updateFn, setState,
          linkedClients, linkedProviders, updateZoneClientsFn, updateZoneProvidersFn),
    },
  }

  return componentCallbacks[component] || {}
}

const ZoneList = () => {
  const [state, setState] = useSetState(defaultState)
  const { defaultCount, loadNext, page, searchFilter, showEditZoneModal } = state

  const modalContext = useModals()
  const {
    callbacks: { setModalData },
  } = modalContext

  const entityReducer = useSelector(reduxState => reduxState.zonesV4)
  const { result: { hasMore }, loading } = entityReducer

  const options = {
    size: defaultCount,
    sort: 'name asc',
    offset: page * defaultCount,
    loadNext,
    name: searchFilter,
  }

  useReduxAction('zonesV4', 'loadZonesV4', options, [searchFilter, page])

  const { filteredZonesV4 } = useZonesV4()

  const updateSearchFilter = (val) => {
    setState({ searchFilter: val, page: 0, loadNext: false })
  }

  const pageContext = {
    callbacks: {
      showEditZoneModal: (payload) => {
        setModalData('EditZoneModal', payload)
        setState({ showEditZoneModal: true })
      },
      searchZoneFilter: val => updateSearchFilter(val),
    },
  }

  const loadMoreZones = () => {
    setState({ page: page + 1, loadNext: true })
  }

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageHeader
          activeTabBarItemKey="zones"
          boxProps={{ height: [112, 105], justifyContent: 'flex-start' }}
          title="Zone Management"
          offset={{ left: 70 }}
        />
        <Box paddingX="large" paddingTop={[112, 105]}>
          <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
            <Columns.Main>
              <DashboardModule title="Zones">
                <Box flexDirection="column" className={custom.scroll}>
                  <Box flexDirection="column" maxHeight="75vh" overflowY="auto">
                    {filteredZonesV4.length > 0 && filteredZonesV4.map(zone => (
                      <Zone key={zone.id} zone={zone} />
                    ))}
                    <LoadingModule loading={loading} boxProps={{ border: 0 }} />
                  </Box>
                  {filteredZonesV4.length > 0 && (
                    <DashboardModule.LoadMoreFooter
                      callbacks={{ loadMore: () => loadMoreZones() }}
                      canLoadMore={hasMore}
                      entityCount={filteredZonesV4?.length}
                    />
                  )}
                  {!loading && !filteredZonesV4.length && (
                    <DashboardModule.BlankState imageUrl={blankState}>
                      <DashboardModule.BlankState.Title>No Zones Available</DashboardModule.BlankState.Title>
                      <DashboardModule.BlankState.Paragraph>
                        Try adjusting your filters.
                      </DashboardModule.BlankState.Paragraph>
                    </DashboardModule.BlankState>
                  )}
                </Box>
              </DashboardModule>
            </Columns.Main>
            <Columns.Sidebar>
              <DashboardModule title="Tools">
                <Tools />
              </DashboardModule>
            </Columns.Sidebar>
          </Columns>
        </Box>
        <EditZoneModal
          callbacks={callbacks('EditZoneModal', setState)}
          showModal={showEditZoneModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default ZoneList
